import { PageSection } from "@app/shared";
import { FC, JSX, useContext, useMemo } from "react";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { ProviderHubContext } from "./contexts/ProviderHubContext";
import WaffleCountBadge from "@app/shared/waffles/WaffleCountBadge";
import { RegionSetSpecialtyGroupsWithGapsTableData } from "./interfaces";

const ProviderHubGapsView: FC = (): JSX.Element => {

   /* eslint-disable sort-keys */
  const regionSetSpecialtyGroupsWithGapsColumnConfig: ColumnConfig = {
    serviceArea: {
      displayText: "Service Area",
      sizeContentToFit: true,
      testId: "provider-hub_gaps-table-service-area"
    },
    specialtyGroup: {
      displayText: "Specialty",
      maxWidth: "200px",
      testId: "provider-hub_gaps-table-specialty",
      wrapContent: true
    },
    providersImpactCount: {
      displayText: "",
      noSort: true,
      sizeContentToFit: true,
      testId: "provider-hub_gaps-table-providers-impact-count"
    }
  }
  /* eslint-enable sort-keys */

  const providerHubContext = useContext(ProviderHubContext);

  const handleCheckboxChange = (async (selectedRegionSetSpecialtyGroupIds: string[]) => {

    providerHubContext.setSelectedRegionSetSpecialtyGroupsWithGaps(selectedRegionSetSpecialtyGroupIds);

    const regionSetSpecialtyGroupsWithGapsDictionary = providerHubContext.regionSetSpecialtyGroupsWithGaps.reduce<Record<string, number[]>>((acc, item) => {
      acc[item.regionSetSpecialtyGroupId.toString()] = item.servicingProviderIdsThatCloseGaps;
      return acc;
    }, {});

    /* Since the same provider could impact different gaps, on those cases we need to make sure the highlight on the providers table remains until the last impacted gap is unselected.
       For this a Set is used, to guarantee the uniqueness of the merged keys */
    const highlightedServicingProviderIds = new Set<number>;

    selectedRegionSetSpecialtyGroupIds.forEach(key => {
      if (regionSetSpecialtyGroupsWithGapsDictionary[key]){
        regionSetSpecialtyGroupsWithGapsDictionary[key].forEach(value => highlightedServicingProviderIds.add(value));
      }
    });

    providerHubContext.setHighlightedServicingProviders(Array.from(highlightedServicingProviderIds).map(String));

  });

   /* eslint-disable sort-keys */
   const gapsTableData: RegionSetSpecialtyGroupsWithGapsTableData[] = useMemo<RegionSetSpecialtyGroupsWithGapsTableData[]>(() => {

    let data: RegionSetSpecialtyGroupsWithGapsTableData[] = [];

    data = providerHubContext.regionSetSpecialtyGroupsWithGaps.map((gap) => {
      return {
        key: gap.regionSetSpecialtyGroupId.toString(),
        serviceArea: gap.serviceArea,
        specialtyGroup: gap.specialtyGroup,
        providersImpactCount: (
          <>
            {/* Do not show the count if only one of the selected providers impacts the highlighted gap  */}
            {providerHubContext.highlightedRegionSetSpecialtyGroupsWithGapsAndCount.some(item => item.regionSetSpecialtyGroupId == gap.regionSetSpecialtyGroupId && item.count > 1) && 
              <WaffleCountBadge backgroundColor="#056edf"
                                count={providerHubContext.highlightedRegionSetSpecialtyGroupsWithGapsAndCount.find(item => item.regionSetSpecialtyGroupId == gap.regionSetSpecialtyGroupId).count}
                                textColor="white" />
            }
          </>
        )
      }
    });

    return data;

  }, [providerHubContext.regionSetSpecialtyGroupsWithGaps, providerHubContext.highlightedRegionSetSpecialtyGroupsWithGapsAndCount]);
  /* eslint-enable sort-keys */

  return (
    <>
      <PageSection className="provider_hub__gaps_view_title"> 
        <h5>Providers</h5>
        <label>Select from the options below to focus your gap search</label>
      </PageSection>

      <PageSection> 
        <label>{providerHubContext.regionSetSpecialtyGroupsWithGaps.length} Gaps</label>
        <Waffles<RegionSetSpecialtyGroupsWithGapsTableData>
          columns={regionSetSpecialtyGroupsWithGapsColumnConfig}
          data={gapsTableData}
          enablePagination={false}
          fullWidth={true}
          handleCheckboxChange={handleCheckboxChange}
          highlightedRows={providerHubContext.highlightedRegionSetSpecialtyGroupsWithGapsAndCount.map(item => item.regionSetSpecialtyGroupId).map(String)}
          rowHighlightColor="#F2F2FC"
          selectedKeys={providerHubContext.selectedRegionSetSpecialtyGroupsWithGaps || []}
          showCheckboxes={true}
          testId="provider-hub__gaps-table"
        />

      </PageSection>

    </>
  )
}

export default ProviderHubGapsView;
